import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Slider,
  Switch,
  Divider,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Grid,
  InputLabel,
  OutlinedInput,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Collapse,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import publisherConfigService, { AdPlacementConfig, PublisherConfig, InlineConfig } from '../services/publisherConfigService';
import { useAuth } from '../contexts/AuthContext';

interface AdPlacementControlProps {
  onConfigUpdate?: (config: AdPlacementConfig) => void;
  config?: AdPlacementConfig;
  onViewPreview?: () => void;
  onViewCode?: () => void;
}

const POSITIONS = [
  'paragraph_end',
  'paragraph_start',
  'after_introduction',
  'before_conclusion',
  'topic_transition',
  'after_code_block',
  'after_list',
  'after_heading'
];

const BLENDING_STYLES = [
  { value: 'conversational', label: 'Conversational' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'distinct', label: 'Distinct' }
];

const FORMAT_OPTIONS = [
  { value: 'auto', label: 'Auto Detect' },
  { value: 'text', label: 'Plain Text' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'html', label: 'HTML' },
  { value: 'json', label: 'JSON' }
];

const APP_TYPES = [
  { value: 'general_chat', label: 'General Chat' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'learning', label: 'Learning Platform' },
  { value: 'customer_support', label: 'Customer Support' },
  { value: 'code_assistant', label: 'Code Assistant' }
];

// Default configuration
const DEFAULT_CONFIG: AdPlacementConfig = {
  mode: 'inline',
  inline_config: {
    tone_matching: true,
    skip_on_factual: true,
    insertion_density: 0.3,
    preferred_positions: ['paragraph_end', 'topic_transition'],
    blending_style: 'subtle'
  },
  response_format: 'auto'
};

// Ensure all inline config fields are present with defaults for missing values
const ensureCompleteInlineConfig = (config: Partial<InlineConfig> = {}): InlineConfig => {
  return {
    tone_matching: config.tone_matching !== undefined ? config.tone_matching : true,
    skip_on_factual: config.skip_on_factual !== undefined ? config.skip_on_factual : true,
    insertion_density: config.insertion_density !== undefined ? config.insertion_density : 0.3,
    preferred_positions: config.preferred_positions || ['paragraph_end', 'topic_transition'],
    blending_style: config.blending_style || 'subtle'
  };
};

const AdPlacementControl: React.FC<AdPlacementControlProps> = ({ 
  onConfigUpdate, 
  config: initialConfig,
  onViewPreview,
  onViewCode
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(!initialConfig);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<AdPlacementConfig>(initialConfig || DEFAULT_CONFIG);
  const [appType, setAppType] = useState<string>('general_chat');

  useEffect(() => {
    // If initialConfig is provided, use it
    if (initialConfig) {
      setConfig({
        ...initialConfig,
        inline_config: initialConfig.inline_config ? ensureCompleteInlineConfig(initialConfig.inline_config) : undefined
      });
      setLoading(false);
      return;
    }
    
    // Otherwise, fetch from API
    const fetchConfig = async () => {
      if (!user?.publisher_id) return;
      
      try {
        const publisherConfig = await publisherConfigService.getPublisherConfig(user.publisher_id);
        
        if (publisherConfig.ad_placement_config) {
          const fetchedConfig = {
            ...publisherConfig.ad_placement_config,
            inline_config: publisherConfig.ad_placement_config.inline_config 
              ? ensureCompleteInlineConfig(publisherConfig.ad_placement_config.inline_config) 
              : undefined
          };
          setConfig(fetchedConfig);
        }
        
        setAppType(publisherConfig.app_type || 'general_chat');
      } catch (err) {
        console.error('Error fetching publisher config', err);
        setError('Unable to load your configuration. Using defaults.');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [user?.publisher_id, initialConfig]);

  // Update local state when initialConfig changes
  useEffect(() => {
    if (initialConfig) {
      setConfig({
        ...initialConfig,
        inline_config: initialConfig.inline_config ? ensureCompleteInlineConfig(initialConfig.inline_config) : undefined
      });
    }
  }, [initialConfig]);

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as 'before' | 'after' | 'inline';
    
    // Create a complete config object
    const updatedConfig: AdPlacementConfig = {
      ...config,
      mode: newMode
    };
    
    // If mode is inline, ensure we have inline_config
    if (newMode === 'inline' && !updatedConfig.inline_config) {
      updatedConfig.inline_config = {
        tone_matching: true,
        skip_on_factual: true,
        insertion_density: 0.3,
        preferred_positions: ['paragraph_end', 'topic_transition'],
        blending_style: 'subtle'
      };
    }
    
    setConfig(updatedConfig);
    
    if (onConfigUpdate) {
      onConfigUpdate(updatedConfig);
    }
  };

  const handleInlineConfigChange = (field: string, value: any) => {
    // Create a complete inline config
    const currentInlineConfig = config.inline_config || {
      tone_matching: true,
      skip_on_factual: true,
      insertion_density: 0.3,
      preferred_positions: ['paragraph_end', 'topic_transition'],
      blending_style: 'subtle'
    };
    
    const newInlineConfig = {
      ...currentInlineConfig,
      [field]: value
    };
    
    const updatedConfig: AdPlacementConfig = {
      ...config,
      inline_config: newInlineConfig
    };
    
    setConfig(updatedConfig);
    
    if (onConfigUpdate) {
      onConfigUpdate(updatedConfig);
    }
  };

  const handleFormatChange = (event: SelectChangeEvent<string>) => {
    const updatedConfig: AdPlacementConfig = {
      ...config,
      response_format: event.target.value as 'text' | 'markdown' | 'html' | 'json' | 'auto'
    };
    
    setConfig(updatedConfig);
    
    if (onConfigUpdate) {
      onConfigUpdate(updatedConfig);
    }
  };

  const handlePositionsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const positions = typeof value === 'string' ? value.split(',') : value;
    
    handleInlineConfigChange('preferred_positions', positions);
  };

  const handleBlendingStyleChange = (event: SelectChangeEvent) => {
    handleInlineConfigChange('blending_style', event.target.value);
  };

  const handleAppTypeChange = async (event: SelectChangeEvent) => {
    const newAppType = event.target.value;
    setAppType(newAppType);
    
    try {
      const defaultConfig = await publisherConfigService.getDefaultConfig(newAppType);
      
      // Ensure the config is complete
      const completeConfig = {
        ...defaultConfig,
        inline_config: defaultConfig.inline_config ? ensureCompleteInlineConfig(defaultConfig.inline_config) : undefined
      };
      
      setConfig(completeConfig);
      
      if (onConfigUpdate) {
        onConfigUpdate(completeConfig);
      }
    } catch (err) {
      console.error('Error fetching default config', err);
    }
  };

  const saveConfig = async () => {
    if (!user?.publisher_id) {
      setError('User ID not found. Please log in again.');
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Ensure we have a complete config object with all required fields
      const completeConfig: AdPlacementConfig = {
        ...config,
        mode: config.mode || 'after',
        response_format: config.response_format || 'auto',
        inline_config: config.mode === 'inline' ? ensureCompleteInlineConfig(config.inline_config || {}) : undefined
      };
      
      const updatedConfig: Partial<PublisherConfig> = {
        ad_placement_config: completeConfig,
        app_type: appType,
        is_active: true
      };
      
      console.log('Saving config:', JSON.stringify(updatedConfig, null, 2));
      
      await publisherConfigService.updatePublisherConfig(user.publisher_id, updatedConfig);
      setSuccess('Configuration saved successfully');
      
      if (onConfigUpdate) {
        onConfigUpdate(completeConfig);
      }
    } catch (err: any) {
      console.error('Error saving configuration', err);
      let errorMessage = 'Failed to save configuration. Please try again.';
      
      // Extract more specific error message if available
      if (err.response && err.response.data && err.response.data.detail) {
        errorMessage = `Error: ${err.response.data.detail}`;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
          Ad Placement Configuration
        </Typography>
        
        <Collapse in={!!error || !!success}>
          <Box sx={{ mb: 2 }}>
            {error && (
              <Alert 
                severity="error"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setError(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert 
                severity="success"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setSuccess(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {success}
              </Alert>
            )}
          </Box>
        </Collapse>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'rgba(0, 0, 0, 0.6)' }}>
          Configure how ads are placed in relation to LLM-generated content. Choose between inserting ads before responses, after responses, or inline within the content.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ 
              mb: 3,
              '& .MuiInputLabel-root': { 
                color: 'rgba(0, 0, 0, 0.6)' 
              },
              '& .MuiOutlinedInput-root': { 
                color: 'rgba(0, 0, 0, 0.87)',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                }
              }
            }}>
              <InputLabel id="app-type-label">Application Type</InputLabel>
              <Select
                labelId="app-type-label"
                id="app-type"
                value={appType}
                onChange={handleAppTypeChange}
                label="Application Type"
              >
                {APP_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ 
              mb: 3,
              '& .MuiInputLabel-root': { 
                color: 'rgba(0, 0, 0, 0.6)' 
              },
              '& .MuiOutlinedInput-root': { 
                color: 'rgba(0, 0, 0, 0.87)',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                }
              }
            }}>
              <InputLabel id="format-label">Response Format</InputLabel>
              <Select
                labelId="format-label"
                id="format"
                value={config.response_format || 'auto'}
                onChange={handleFormatChange}
                label="Response Format"
              >
                {FORMAT_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, color: 'rgba(0, 0, 0, 0.87)' }}>
          Ad Placement Mode
        </Typography>
        
        <FormControl component="fieldset" sx={{ 
          mb: 3,
          '& .MuiFormControlLabel-root': {
            color: 'rgba(0, 0, 0, 0.87)'
          },
          '& .MuiRadio-root': {
            color: 'rgba(0, 0, 0, 0.54)'
          }
        }}>
          <RadioGroup
            row
            name="placement-mode"
            value={config.mode}
            onChange={handleModeChange}
          >
            <FormControlLabel value="before" control={<Radio />} label="Before LLM Response" />
            <FormControlLabel value="after" control={<Radio />} label="After LLM Response" />
            <FormControlLabel value="inline" control={<Radio />} label="Inline with Content" />
          </RadioGroup>
        </FormControl>
        
        {config.mode === 'inline' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2, color: 'rgba(0, 0, 0, 0.87)' }}>
              Inline Placement Options
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography id="density-slider-label" gutterBottom sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                    Insertion Density: {config.inline_config?.insertion_density || 0.3}
                  </Typography>
                  <Slider
                    value={config.inline_config?.insertion_density || 0.3}
                    onChange={(_, newValue) => handleInlineConfigChange('insertion_density', newValue)}
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    marks={[
                      { value: 0.1, label: 'Low' },
                      { value: 0.5, label: 'Medium' },
                      { value: 1.0, label: 'High' }
                    ]}
                    valueLabelDisplay="auto"
                    aria-labelledby="density-slider-label"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ 
                  mb: 3,
                  '& .MuiInputLabel-root': { 
                    color: 'rgba(0, 0, 0, 0.6)' 
                  },
                  '& .MuiOutlinedInput-root': { 
                    color: 'rgba(0, 0, 0, 0.87)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    }
                  }
                }}>
                  <InputLabel id="blending-style-label">Blending Style</InputLabel>
                  <Select
                    labelId="blending-style-label"
                    id="blending-style"
                    value={config.inline_config?.blending_style || 'subtle'}
                    onChange={handleBlendingStyleChange}
                    label="Blending Style"
                  >
                    {BLENDING_STYLES.map(style => (
                      <MenuItem key={style.value} value={style.value}>{style.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ 
                  mb: 3,
                  '& .MuiInputLabel-root': { 
                    color: 'rgba(0, 0, 0, 0.6)' 
                  },
                  '& .MuiOutlinedInput-root': { 
                    color: 'rgba(0, 0, 0, 0.87)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    }
                  }
                }}>
                  <InputLabel id="positions-label">Preferred Positions</InputLabel>
                  <Select
                    labelId="positions-label"
                    id="positions"
                    multiple
                    value={config.inline_config?.preferred_positions || []}
                    onChange={handlePositionsChange}
                    input={<OutlinedInput id="select-multiple-positions" label="Preferred Positions" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value.replace('_', ' ')} />
                        ))}
                      </Box>
                    )}
                  >
                    {POSITIONS.map((position) => (
                      <MenuItem key={position} value={position}>
                        {position.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.inline_config?.tone_matching || false}
                      onChange={(e) => handleInlineConfigChange('tone_matching', e.target.checked)}
                    />
                  }
                  label="Match Tone of Content"
                  sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.inline_config?.skip_on_factual || false}
                      onChange={(e) => handleInlineConfigChange('skip_on_factual', e.target.checked)}
                    />
                  }
                  label="Skip Factual Content"
                  sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                />
              </Grid>
            </Grid>
          </>
        )}
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          {onViewCode && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={onViewCode}
              sx={{ mr: 2 }}
            >
              Generate Code
            </Button>
          )}
          {onViewPreview && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onViewPreview}
              sx={{ mr: 2 }}
            >
              View Preview
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={saveConfig}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdPlacementControl; 